const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const INTEREST_ALIASES = {
  "Assisted Living/Memory Care": "Assisted Living / Memory Care",
  "Skilled Nursing/Rehab": "Skilled Nursing / Rehab",
  "Skilled Nursing / Rehab": "Skilled Nursing / Rehab",
};

const LOCATION_ALIASES = {
  "McKee Apartments": "The Alee, Fitchburg",
  "McKee Apartments, Fitchburg": "The Alee, Fitchburg",
  "The Alee": "The Alee, Fitchburg",
  "Auburn Homes & Services Chaska": "Auburn Homes & Services - Chaska",
  "Auburn Homes & Services Waconia": "Auburn Homes & Services - Waconia",
  "The Cesta": "The Cesta - Fitchburg",
  "St. Anne’s - Milwaukee": "St. Anne's - Milwaukee",
  "St. Anne’s": "St. Anne's - Milwaukee",
  "Lutheran Homes and Health Services":
    "Lutheran Homes and Health Services - Fond du Lac",
  "The Marquardt": "The Marquardt - Watertown",
};

export const normalizeInterest = (value = "") =>
  INTEREST_ALIASES[value] || value;

export const normalizeLocation = (value = "") => LOCATION_ALIASES[value] || value;

const findInterest = (config, interest) => {
  const canonicalInterest = normalizeInterest(interest);
  return config?.interests?.find((item) => item.value === canonicalInterest);
};

const findLocation = (config, interest, location) => {
  const entry = findInterest(config, interest);
  const canonicalLocation = normalizeLocation(location);
  return entry?.locations?.find((item) => item.value === canonicalLocation);
};

const findLocationByPageHandle = (locations, pageHandle) =>
  (locations || []).find((item) => item.pageHandle === pageHandle);

/**
 * Resolve a marketing page's community handle to its canonical lead-capture
 * location value. Landing pages know their community by slug (e.g. "chaska"),
 * never by the display title, which carries SEO copy that matches nothing.
 * Scoped to `interest` when given so a page can only resolve to a location that
 * interest actually serves.
 */
export const getLocationValueByPageHandle = (config, pageHandle, interest) => {
  if (!pageHandle) return null;

  const scoped = interest
    ? [findInterest(config, interest)].filter(Boolean)
    : config?.interests || [];

  for (const entry of scoped) {
    const match = findLocationByPageHandle(entry.locations, pageHandle);
    if (match) return match.value;
  }

  return null;
};

export const getInterestValues = (config) =>
  (config?.interests || []).map((interest) => interest.value);

export const getLocationsForInterest = (config, interest) =>
  (findInterest(config, interest)?.locations || []).map((location) => location.value);

export const getInterestsForLocation = (config, location) => {
  const canonicalLocation = normalizeLocation(location);
  return (config?.interests || [])
    .filter((interest) =>
      interest.locations?.some((item) => item.value === canonicalLocation),
    )
    .map((interest) => interest.value);
};

export const getEmailRecipients = (config, interest, location) =>
  findLocation(config, interest, location)?.emailRecipients || [];

export const getWelcomeHomeDestination = (config, interest, location) =>
  findLocation(config, interest, location)?.welcomeHomeDestination || null;

export const getWelcomeHomeRoute = (config, interest, location) => {
  const interestEntry = findInterest(config, interest);
  const locationEntry = findLocation(config, interest, location);

  if (!interestEntry?.welcomeHome?.enabled || !locationEntry?.welcomeHome) {
    return null;
  }

  return {
    enabled: true,
    careTypeId: interestEntry.welcomeHome.careTypeId,
    communityId: locationEntry.welcomeHome.communityId,
    credentialKey: locationEntry.welcomeHome.credentialKey || "default",
  };
};

/**
 * Why a submission will or will not reach WelcomeHome. Distinguishes a
 * deliberate skip ("no-integration" — Careers, Giving, Affordable Housing) from
 * a routing gap ("unknown-interest" / "unknown-location"), which always means a
 * form is submitting a value the config does not know about.
 */
export const getRoutingStatus = (config, interest, location) => {
  const interestEntry = findInterest(config, interest);
  if (!interestEntry) return "unknown-interest";

  const locationEntry = findLocation(config, interest, location);
  if (!locationEntry) return "unknown-location";

  if (
    !interestEntry.welcomeHome?.enabled ||
    !locationEntry.welcomeHome?.communityId
  ) {
    return "no-integration";
  }

  return "routable";
};

const isUsableRecipient = (email) =>
  typeof email === "string" && email.trim() !== "";

/**
 * Strip blank recipient rows before validation.
 *
 * Sanity's array editor adds an empty row the moment someone clicks "Add item",
 * and validation is all-or-nothing: one blank field would otherwise invalidate
 * the whole document and silently drop every community back to the hardcoded
 * fallback. A blank row carries no intent, so drop it rather than fail on it.
 * Genuinely malformed addresses still fail validation.
 */
export const sanitizeLeadCaptureConfig = (config) => {
  let removedRecipients = 0;

  const interests = (config?.interests || []).map((interest) => ({
    ...interest,
    locations: (interest?.locations || []).map((location) => {
      if (!Array.isArray(location?.emailRecipients)) return location;

      const kept = location.emailRecipients
        .filter(isUsableRecipient)
        .map((email) => email.trim());
      removedRecipients += location.emailRecipients.length - kept.length;

      return kept.length === location.emailRecipients.length
        ? location
        : { ...location, emailRecipients: kept };
    }),
  }));

  return { config: { ...config, interests }, removedRecipients };
};

export const validateLeadCaptureConfig = (config) => {
  const errors = [];
  const interests = config?.interests;

  if (!Array.isArray(interests) || interests.length === 0) {
    return { valid: false, errors: ["interests must be a non-empty array"] };
  }

  const interestValues = new Set();
  interests.forEach((interest, interestIndex) => {
    if (!interest?.value || !interest?.label) {
      errors.push(`interest ${interestIndex} is missing value or label`);
    }
    if (interestValues.has(interest?.value)) {
      errors.push(`duplicate interest: ${interest.value}`);
    }
    interestValues.add(interest?.value);

    if (interest?.welcomeHome?.enabled && !Number.isInteger(interest.welcomeHome.careTypeId)) {
      errors.push(`enabled interest is missing careTypeId: ${interest.value}`);
    }

    const locationValues = new Set();
    (interest?.locations || []).forEach((location, locationIndex) => {
      if (!location?.value || !location?.label || location.value === "N/A") {
        errors.push(`invalid location at ${interestIndex}:${locationIndex}`);
      }
      if (locationValues.has(location?.value)) {
        errors.push(`duplicate location in ${interest.value}: ${location.value}`);
      }
      locationValues.add(location?.value);

      if (!Array.isArray(location?.emailRecipients)) {
        errors.push(`emailRecipients must be an array: ${interest.value}/${location?.value}`);
      } else if (
        location.emailRecipients.some((email) => !EMAIL_PATTERN.test(email))
      ) {
        errors.push(`invalid email recipient: ${interest.value}/${location.value}`);
      }
    });
  });

  return { valid: errors.length === 0, errors };
};

export const toPublicLeadCaptureConfig = (config) => ({
  interests: (config?.interests || []).map((interest) => ({
    value: interest.value,
    label: interest.label,
    locations: (interest.locations || []).map((location) => ({
      value: location.value,
      label: location.label,
      pageHandle: location.pageHandle,
    })),
  })),
});
